// @ts-strict-ignore
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackBar } from '@usealloy/component-library';

import type { CustomQueryOpts } from '~/helpers/api-queries/@types/utils';
import apiCalls from '~/helpers/apiCalls';
import { useModal } from '~/journey/ModalContext';

import { transformOutcomeFormData } from './helpers/formHelpers';
import type {
  Outcome,
  OutcomeWithUsage,
  SubmittedFormData,
  SortTuple,
  OutcomesQueryOptions,
} from './types';

export const OUTCOMES_API_MAX_LIMIT = 500;

/**
 * Using `useGetOutcomes` outside of the settings view will pass includeUsage as false
 * All other hooks are used on the settings page, where includeUsage should be true
 */
export const getQueryKey = (includeArchived, pagination, includeUsage = true, sorting = null) => ['outcomes', includeArchived, pagination, includeUsage, sorting];

interface OutcomeData {
  count: number;
  outcomes: (Outcome | OutcomeWithUsage)[];
}

interface Error {
  responseError: {
    message: string;
  };
}

type Opts<
  TData = OutcomeData,
  TErr = unknown,
  TSel = TData,
> = Pick<CustomQueryOpts<TData, TErr, TSel>, 'select'> & {
  includeAlertOutcomes?: boolean;
  includeArchived?: boolean;
  includeEventStatusOutcomes?: boolean;
  includeUsage?: boolean;
  includeWorkflowOutcomes?: boolean;
  pagination?: {
    pageIndex?: number;
    pageSize?: number;
  };
  shouldEnableQuery?: boolean;
  sorting?: SortTuple | SortTuple[] | undefined;
  typhonDomain: string;
};

export const useGetOutcomes = <
  TData extends OutcomeData,
  TErr extends Error = Error,
  TSel = TData,
>(params: Opts<TData, TErr, TSel>) => {
  const {
    includeAlertOutcomes = false,
    includeArchived = false,
    includeEventStatusOutcomes = false,
    includeUsage = false,
    includeWorkflowOutcomes = false,
    pagination = null,
    select = null,
    shouldEnableQuery = true,
    sorting = null,
    typhonDomain,
  } = params;
  const addSnackBar = useSnackBar();

  return useQuery<OutcomeData, TErr, TSel>({
    enabled: shouldEnableQuery,
    initialData: { outcomes: [], count: 0 },
    queryKey: getQueryKey(includeArchived, pagination, includeUsage, sorting),
    queryFn: async () => {
      const hasSort = Array.isArray(sorting) && sorting.length > 0;
      const firstSort = hasSort ? sorting[0] : null;
      // sort param: [field, 'ASC'|'DESC']
      let sortParam: SortTuple | SortTuple[] | undefined;

      if (Array.isArray(sorting) && sorting.length > 0) {
        const tuples: SortTuple[] = sorting.map(s => [
          s.id === 'classification' ? 'outcome_classification' : s.id,
          s.desc ? 'DESC' : 'ASC',
        ] as SortTuple);
        sortParam = tuples.length === 1 ? tuples[0] : tuples;
      }

      const options: OutcomesQueryOptions = {
        ...(pagination && { pageNum: pagination.pageIndex + 1 }),
        numRowsPerPage: pagination?.pageSize || OUTCOMES_API_MAX_LIMIT,
        q: {
          includeAlertOutcomes,
          includeArchived,
          includeEventStatusOutcomes,
          includeUsage,
          includeWorkflowOutcomes,
        },
      };

      if (firstSort) {
        options.sort = sortParam;
      }

      const response = await apiCalls.settings.getOutcomesNew({
        typhonDomain,
        options,
      });

      const { _embedded: { pagination: { total } } } = response;
      return { outcomes: response.outcomes, count: total };
    },
    onError: ({ responseError }) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? 'Unexpected Error when fetching outcomes',
        type: 'danger',
      });
    },
    select,
  });
};

export const useArchiveOutcome = (
  typhonDomain,
  includeArchived,
  pagination = null,
  sorting = null,
) => {
  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    includeArchived,
    pagination,
    true,
    sorting,
  );
  return useMutation({
    mutationFn: async (row: OutcomeWithUsage) => {
      const encodedOutcome = encodeURIComponent(row.outcome);
      const res = await apiCalls.settings.archiveOutcome({
        typhonDomain,
        encodedOutcome,
        customerOutcomeId: row.customer_outcome_id,
      });
      return res.outcome;
    },
    onSuccess: (data, row: OutcomeWithUsage) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { outcomes: OutcomeWithUsage[]; count: number }) => {
          const { outcomes, count } = oldData;
          const newOutcomes = outcomes.map(outcome => (
            outcome.outcome === row.outcome
              // The archive endpoint does not check usage, so we use the usage and can_be_deleted
              // from the original outcome to maintain the same state
              ? {
                ...data,
                // eslint-disable-next-line no-underscore-dangle
                _embedded: outcome._embedded,
                can_be_deleted: outcome.can_be_deleted,
              }
              : outcome
          ));
          return { outcomes: newOutcomes, count };
        });
      addSnackBar({
        title: 'Successfully archived outcome',
        type: 'success',
      });
    },
    onError: ({ responseError }, row) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? `Error archiving ${row.outcome} outcome, check to see if the outcome is in use`,
        type: 'danger',
      });
    },
  });
};

export const useUnarchiveOutcome = (
  typhonDomain,
  includeArchived,
  pagination = null,
  sorting = null,
) => {
  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    includeArchived,
    pagination,
    true,
    sorting,
  );
  return useMutation({
    mutationFn: async (row: OutcomeWithUsage) => {
      const encodedOutcome = encodeURIComponent(row.outcome);
      const res = await apiCalls.settings.unarchiveOutcome({
        typhonDomain,
        encodedOutcome,
        customerOutcomeId: row.customer_outcome_id,
      });
      return res.outcome;
    },
    onSuccess: (data, row: OutcomeWithUsage) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { outcomes: OutcomeWithUsage[]; count: number }) => {
          const { outcomes, count } = oldData;
          const newOutcomes = outcomes.map(outcome => (
            outcome.outcome === row.outcome
              // The unarchive endpoint does not check usage, but since the outcome can't have been archived if there was usage,
              // we can assume that it is still unused (for now) and can be safely archived
              // Also maintain `can_be_deleted`, since that won't change
              ? {
                ...data,
                // eslint-disable-next-line no-underscore-dangle
                _embedded: outcome._embedded,
                can_be_archived: true,
                can_be_deleted: outcome.can_be_deleted,
              }
              : outcome
          ));
          return { outcomes: newOutcomes, count };
        });
      addSnackBar({
        title: 'Successfully unarchived outcome',
        type: 'success',
      });
    },
    onError: ({ responseError }, row) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? `Unexpected Error when unarchiving ${row.outcome} outcome`,
        type: 'danger',
      });
    },
  });
};

export const useAddOutcome = (
  typhonDomain,
  includeArchived,
  pagination = null,
  sorting = null,
) => {
  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    includeArchived,
    pagination,
    true,
    sorting,
  );
  const { close } = useModal();

  return useMutation({
    mutationFn: async (outcomeFormData: SubmittedFormData) => {
      const outcome = transformOutcomeFormData(outcomeFormData);
      const res = await apiCalls.settings.createOutcome({
        outcome,
        typhonDomain,
      });
      return res.outcome;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { outcomes: OutcomeWithUsage[]; count: number }) => {
          const { outcomes, count } = oldData;
          // Prepend newly created outcome
          const newOutcome: OutcomeWithUsage = {
            ...(data as OutcomeWithUsage),
            can_be_deleted: true,
            can_be_archived: true,
          };
          return { outcomes: [newOutcome, ...outcomes], count: count + 1 };
        });
      close();
      addSnackBar({
        title: 'Successfully added outcome',
        type: 'success',
      });
    },
    onError: ({ responseError }) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? 'Unexpected Error creating new outcome',
        type: 'danger',
      });
    },
  });
};


export const useUpdateOutcome = (
  typhonDomain,
  includeArchived,
  pagination = null,
  sorting = null,
) => {
  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    includeArchived,
    pagination,
    true,
    sorting,
  );
  const { close } = useModal();
  return useMutation({
    mutationFn: async (outcomeFormData: SubmittedFormData) => {
      const outcome = transformOutcomeFormData(outcomeFormData);
      const encodedOutcome = encodeURIComponent(outcome.outcome);
      const res = await apiCalls.settings.updateOutcome({
        typhonDomain,
        encodedOutcome,
        outcomeData: { ...outcome, customer_outcome_id: outcomeFormData.customerOutcomeId },
      });
      return res.outcome;
    },
    onSuccess: (data, outcomeFormData: SubmittedFormData) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { outcomes: OutcomeWithUsage[]; count: number }) => {
          const { outcomes, count } = oldData;
          const newOutcomes = outcomes.map(outcome => (
            outcome.outcome === outcomeFormData.displayName
              // The PUT endpoint does not return usage data, so we keep the existing usage here
              ? {
                ...(data as OutcomeWithUsage),
                // eslint-disable-next-line no-underscore-dangle
                _embedded: outcome._embedded,
                can_be_archived: outcome.can_be_archived,
                can_be_deleted: outcome.can_be_deleted,
              }
              : outcome
          ));
          return { outcomes: newOutcomes, count };
        });
      addSnackBar({
        title: 'Successfully edited outcome',
        type: 'success',
      });
      close();
    },
    onError: ({ responseError }, outcomeFormData: SubmittedFormData) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? `Unexpected Error when editing ${outcomeFormData.displayName} outcome`,
        type: 'danger',
      });
    },
  });
};

export const useDeleteOutcome = (
  typhonDomain,
  includeArchived,
  pagination = null,
  sorting = null,
) => {
  const addSnackBar = useSnackBar();
  const queryClient = useQueryClient();
  const queryKey = getQueryKey(
    includeArchived,
    pagination,
    true,
    sorting,
  );
  return useMutation({
    mutationFn: async (row: OutcomeWithUsage) => {
      const encodedOutcome = encodeURIComponent(row.outcome);
      await apiCalls.settings.deleteOutcome({
        typhonDomain,
        encodedOutcome,
        customerOutcomeId: row.customer_outcome_id,
      });
      return row.outcome;
    },
    onSuccess: (deletedOutcomeName: string) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { outcomes: OutcomeWithUsage[]; count: number }) => {
          const { outcomes, count } = oldData;
          const newOutcomes = outcomes.filter(outcome => (
            outcome.outcome !== deletedOutcomeName
          ));
          const newCount = count - 1;
          return { outcomes: newOutcomes, count: newCount };
        });
      addSnackBar({
        title: 'Successfully deleted outcome',
        type: 'success',
      });
    },
    onError: ({ responseError }, row: OutcomeWithUsage) => {
      const errorMessage = responseError?.message;
      addSnackBar({
        title: errorMessage ?? `Error deleting ${row.outcome} outcome, check to see if the outcome is in use`,
        type: 'danger',
      });
    },
  });
};
